/**
 * Rol "Supervisor Inmobiliaria": colaborador de una inmobiliaria con acceso de
 * lectura a su portal. Lo da de alta el dueño (rol 4) desde
 * /admin/portal-inmobiliaria/equipo.
 *
 * Se detecta por NOMBRE: el id del rol difiere entre dev y prod.
 * La inmobiliaria que supervisa vive en `usuarios.id_persona_inmobiliaria` y se
 * resuelve en BD con la RPC `current_inmob_gestor_persona()`.
 */

export const NOMBRE_ROL_SUPERVISOR_INMOBILIARIA = "Supervisor Inmobiliaria";

export const RUTA_EQUIPO_INMOBILIARIA = "/admin/portal-inmobiliaria/equipo";

/** ¿El nombre de rol es el de Supervisor Inmobiliaria? (sin importar mayúsculas/espacios) */
export function esRolSupervisorInmobiliaria(rolNombre: string | null | undefined): boolean {
  return (rolNombre ?? "").trim().toLowerCase() === NOMBRE_ROL_SUPERVISOR_INMOBILIARIA.toLowerCase();
}

/** Busca el id del rol en una lista `roles` (id, nombre). null si no existe en este ambiente. */
export function idRolSupervisorInmobiliaria(
  roles: { id: number; nombre: string | null }[] | null | undefined,
): number | null {
  return (roles ?? []).find((r) => esRolSupervisorInmobiliaria(r.nombre))?.id ?? null;
}

export interface NuevoSupervisorInput {
  nombre: string;
  email: string;
  telefono?: string | null;
}

/**
 * Body que se manda a la edge function `create-user` para el alta de un
 * supervisor. Mismo contrato que el alta de operadores de banco
 * (useBancoEquipo), cambiando `id_banco` por `id_persona_inmobiliaria`.
 * ÚNICO punto a ajustar si la edge cambia su contrato.
 */
export function armarBodyCrearSupervisor(params: {
  input: NuevoSupervisorInput;
  rolId: number;
  idPersonaInmobiliaria: number;
}): Record<string, unknown> {
  // Sin id_persona: la EF crea o reutiliza la persona del supervisor (una inmobiliaria no puede
  // imponerla). La EF valida que id_persona_inmobiliaria sea la inmobiliaria de quien llama.
  const { input, rolId, idPersonaInmobiliaria } = params;
  return {
    email: input.email.toLowerCase().trim(),
    nombre: input.nombre.trim(),
    rol_id: rolId,
    id_persona_inmobiliaria: idPersonaInmobiliaria,
    telefono: input.telefono?.trim() || undefined,
    clave_pais_telefono: input.telefono?.trim() ? "MX" : undefined,
  };
}

/**
 * ¿El error de PostgREST indica que la función o columna aún no existe en este
 * ambiente (migración sin aplicar)? Patrón "DDL probe".
 */
export function esErrorObjetoInexistente(error: { code?: string | null; message?: string | null } | null | undefined): boolean {
  if (!error) return false;
  const code = error.code ?? "";
  if (code === "PGRST202" || code === "42883" || code === "42P01" || code === "42703" || code === "PGRST204") return true;
  const msg = (error.message ?? "").toLowerCase();
  return msg.includes("could not find the function") || msg.includes("does not exist");
}
